/*
 * Vault Aroma VaultKey
 * Woodstock 3.3.0 rebuild — 2026-07
 * Numbers-only access for permanent VAK-#### fragrance records.
 * No third-party dependencies, cookies, local storage, or remote requests.
 */
(() => {
  'use strict';

  const PREFIX = 'VAK';
  const DIGIT_COUNT = 4;

  const digitsOnly = (value) =>
    String(value || '')
      .normalize('NFKD')
      .replace(/\D/g, '')
      .slice(-DIGIT_COUNT);

  const fullKey = (digits) => `${PREFIX}-${digits}`;
  const safeArray = (value) => (Array.isArray(value) ? value : []);

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  };

  const svgArrow = () => {
    const wrapper = document.createElement('span');
    wrapper.className = 'vk-arrow';
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path d="M5 12h13M13 6l6 6-6 6"/></svg>';
    return wrapper;
  };

  const bottleBackdrop = () => {
    const backdrop = document.createElement('div');
    backdrop.className = 'vk-record__bottle-backdrop';
    backdrop.setAttribute('aria-hidden', 'true');
    backdrop.innerHTML = `
      <svg viewBox="0 0 600 820" focusable="false" role="presentation">
        <defs>
          <linearGradient id="vkBottleGold" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stop-color="#8e6800"/>
            <stop offset="0.5" stop-color="#f2b705"/>
            <stop offset="1" stop-color="#8e6800"/>
          </linearGradient>
          <linearGradient id="vkBottleGlass" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stop-color="#f2b705" stop-opacity="0.17"/>
            <stop offset="0.46" stop-color="#17130a" stop-opacity="0.28"/>
            <stop offset="1" stop-color="#030303" stop-opacity="0.18"/>
          </linearGradient>
          <filter id="vkBottleGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="8" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <g filter="url(#vkBottleGlow)">
          <rect x="205" y="24" width="190" height="104" rx="3" fill="#090909" stroke="#f2b705" stroke-width="4"/>
          <rect x="238" y="128" width="124" height="82" fill="url(#vkBottleGold)"/>
          <circle cx="300" cy="76" r="8" fill="none" stroke="#dcae2a" stroke-width="2.2"/>
          <path d="M126 222H474L520 678Q525 752 447 770H153Q75 752 80 678Z" fill="url(#vkBottleGlass)" stroke="#dcae2a" stroke-width="5"/>
          <path d="M126 222H474" stroke="#f2b705" stroke-width="7"/>
          <rect x="160" y="395" width="280" height="245" rx="8" fill="#080808" fill-opacity="0.38" stroke="#f2b705" stroke-opacity="0.48" stroke-width="2"/>
          <path d="M121 786H479" stroke="#f2b705" stroke-opacity="0.34" stroke-width="1"/>
        </g>
      </svg>`;
    return backdrop;
  };

  const formatDate = (value) => {
    if (!value) return '';
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return value;
    return new Intl.DateTimeFormat(document.documentElement.lang || 'en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(parsed);
  };

  class VaultKeyEntry extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.form = this.querySelector('[data-vk-entry-form]');
      this.input = this.querySelector('[data-vk-entry-input]');
      this.error = this.querySelector('[data-vk-entry-error]');

      if (!this.form || !this.input) return;

      this.input.addEventListener('input', () => {
        const clean = digitsOnly(this.input.value);
        if (this.input.value !== clean) this.input.value = clean;
        this.input.setAttribute('aria-invalid', 'false');
        if (this.error) this.error.textContent = '';
      });

      this.input.addEventListener('paste', (event) => {
        const clean = digitsOnly(event.clipboardData?.getData('text') || '');
        if (!clean) return;
        event.preventDefault();
        this.input.value = clean;
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
      });

      this.form.addEventListener('submit', (event) => {
        const digits = digitsOnly(this.input.value);
        if (digits.length !== DIGIT_COUNT) {
          event.preventDefault();
          this.input.setAttribute('aria-invalid', 'true');
          if (this.error) {
            this.error.textContent = this.dataset.invalidMessage || 'Enter all four digits from your VaultKey.';
          }
          this.input.focus({ preventScroll: true });
          return;
        }

        this.input.value = digits;
      });
    }
  }

  class VaultKeyLocator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.form = this.querySelector('[data-vk-locator-form]');
      this.input = this.querySelector('[data-vk-locator-input]');
      this.status = this.querySelector('[data-vk-locator-status]');
      this.results = this.querySelector('[data-vk-locator-results]');
      this.records = this.readRecords();
      this.index = new Map();
      this.currentRecord = null;

      if (!this.form || !this.input || !this.status || !this.results) return;

      this.records.forEach((record) => {
        if (record.digits && !this.index.has(record.digits)) this.index.set(record.digits, record);
      });

      this.input.addEventListener('input', () => {
        const clean = digitsOnly(this.input.value);
        if (this.input.value !== clean) this.input.value = clean;
        this.input.setAttribute('aria-invalid', 'false');
        this.setStatus('', 'idle');
      });

      this.input.addEventListener('paste', (event) => {
        const clean = digitsOnly(event.clipboardData?.getData('text') || '');
        if (!clean) return;
        event.preventDefault();
        this.input.value = clean;
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
      });

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.lookup(this.input.value, true);
      });

      this.results.addEventListener('click', (event) => {
        const copyButton = event.target.closest('[data-vk-copy]');
        if (copyButton && this.currentRecord) this.copyLink(copyButton);
      });

      const queryValue = this.readQueryValue();
      if (queryValue) {
        this.input.value = digitsOnly(queryValue);
        this.lookup(queryValue, false);
      }
    }

    readRecords() {
      const data = this.querySelector('[data-vk-records]');
      if (!data) return [];

      try {
        const parsed = JSON.parse(data.textContent || '[]');
        if (!Array.isArray(parsed)) return [];

        return parsed
          .filter((record) => record && record.key && record.name)
          .map((record) => ({
            key: String(record.key || '').trim(),
            digits: digitsOnly(record.key),
            brand: String(record.brand || '').trim(),
            name: String(record.name || '').trim(),
            concentration: String(record.concentration || '').trim(),
            edition: String(record.edition || '').trim(),
            ingredients: String(record.ingredients || '').trim(),
            warning: String(record.warning || '').trim(),
            verifiedOn: String(record.verifiedOn || '').trim(),
            formulationVersion: String(record.formulationVersion || '').trim(),
            recordStatus: String(record.recordStatus || '').trim(),
            statusNote: String(record.statusNote || '').trim(),
            replacementKey: String(record.replacementKey || '').trim(),
            productUrl: String(record.productUrl || '').trim(),
            productTitle: String(record.productTitle || '').trim(),
            priceText: String(record.priceText || '').trim(),
            productAvailable: record.productAvailable !== false,
            imageUrl: String(record.imageUrl || '').trim(),
            imageAlt: String(record.imageAlt || '').trim(),
            aliases: safeArray(record.aliases).map((alias) => String(alias || '').trim()).filter(Boolean),
            handle: String(record.handle || '').trim(),
            demo: Boolean(record.demo)
          }))
          .filter((record) => record.digits.length === DIGIT_COUNT);
      } catch (error) {
        console.error('[VaultKey] Record data could not be parsed.', error);
        return [];
      }
    }

    readQueryValue() {
      const params = new URLSearchParams(window.location.search);
      return params.get('vk') || params.get('vaultkey') || params.get('key') || '';
    }

    lookup(rawValue, updateAddress) {
      const digits = digitsOnly(rawValue);
      this.currentRecord = null;
      this.results.replaceChildren();
      this.results.hidden = true;

      if (!digits) {
        this.showError(this.dataset.emptyMessage || 'Enter the four digits printed after VAK-.');
        return;
      }

      if (digits.length !== DIGIT_COUNT) {
        this.showError(this.dataset.invalidMessage || 'A VaultKey contains exactly four digits.');
        return;
      }

      if (this.records.length === 0) {
        this.showError(this.dataset.noRecordsMessage || 'No active VaultKey records are available yet.');
        return;
      }

      const record = this.index.get(digits);
      if (!record) {
        this.showError(this.dataset.notFoundMessage || 'That VaultKey was not found. Check the four digits and try again.');
        return;
      }

      this.input.value = digits;
      this.input.setAttribute('aria-invalid', 'false');
      this.currentRecord = record;
      this.renderRecord(record);
      this.setStatus(this.dataset.foundMessage || 'Fragrance record confirmed.', 'success');
      if (updateAddress) this.updateAddress(record.key || fullKey(digits));
    }

    showError(message) {
      this.input.setAttribute('aria-invalid', 'true');
      this.setStatus(message, 'error');
      this.input.focus({ preventScroll: true });
    }

    setStatus(message, state) {
      this.status.textContent = message;
      this.status.dataset.state = state;
    }

    updateAddress(key) {
      const url = new URL(window.location.href);
      url.searchParams.set('vk', key);
      window.history.replaceState({}, '', url);
    }

    renderRecord(record) {
      const card = this.buildRecord(record);
      this.results.replaceChildren(card);
      this.results.hidden = false;

      requestAnimationFrame(() => {
        this.results.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start'
        });
      });
    }

    buildRecord(record) {
      const article = el('article', 'vk-record');
      article.dataset.recordStatus = (record.recordStatus || 'current').toLowerCase().replace(/\s+/g, '-');

      const header = el('header', 'vk-record__header');
      const identity = el('div', 'vk-record__identity');
      identity.append(
        el('span', 'vk-record__identity-label', this.dataset.recordLabel || 'Vault Aroma Fragrance Record'),
        el('strong', 'vk-record__identity-key', record.key || fullKey(record.digits))
      );

      const state = el('div', 'vk-record__state');
      state.append(
        el('span', 'vk-record__state-dot'),
        el('span', 'vk-record__state-text', record.demo ? 'Demonstration' : (record.recordStatus || this.dataset.defaultStatus || 'Record active'))
      );
      header.append(identity, state);

      const core = el('div', 'vk-record__core');
      const media = this.buildMedia(record);
      const content = this.buildContent(record);
      core.append(media, content);

      const lower = el('div', 'vk-record__lower');
      lower.append(this.buildIngredients(record), this.buildRecordNotes(record));

      const process = this.buildDocumentationProcess();

      const footer = el('footer', 'vk-record__footer');
      const disclaimer = el('p', 'vk-record__disclaimer', this.dataset.packagedBy || 'Rebottled and packaged by Vault Aroma Vanta Inc. Product names and trademarks remain the property of their respective owners.');
      const copy = el('button', 'vk-record__copy', this.dataset.copyLabel || 'Copy record link');
      copy.type = 'button';
      copy.setAttribute('data-vk-copy', '');
      copy.append(svgArrow());
      footer.append(disclaimer, copy);

      article.append(header, core, lower, process, footer);
      return article;
    }

    buildMedia(record) {
      const media = el('div', 'vk-record__media');
      const rail = el('div', 'vk-record__media-rail');
      rail.append(
        el('span', '', 'VAULT AROMA'),
        el('span', '', record.digits)
      );

      const stage = el('div', 'vk-record__image-stage');
      const bottleCanvas = el('div', 'vk-record__bottle-canvas');
      const productWindow = el('div', 'vk-record__product-window');
      bottleCanvas.append(bottleBackdrop(), productWindow);

      if (record.imageUrl) {
        const image = el('img', 'vk-record__image');
        image.src = record.imageUrl;
        image.alt = record.imageAlt || [record.brand, record.name].filter(Boolean).join(' ');
        image.loading = 'eager';
        image.decoding = 'async';
        image.width = 900;
        image.height = 1100;
        productWindow.append(image);
      } else {
        const placeholder = el('div', 'vk-record__placeholder');
        placeholder.append(
          el('span', 'vk-record__placeholder-code', record.digits),
          el('span', 'vk-record__placeholder-copy', this.dataset.imagePendingLabel || 'Fragrance image available upon release.')
        );
        productWindow.append(placeholder);
      }

      stage.append(bottleCanvas);

      const caption = el('div', 'vk-record__media-caption');
      caption.append(
        el('span', '', this.dataset.connectedLabel || 'Connected fragrance'),
        el('span', '', record.productTitle || record.name)
      );

      media.append(rail, stage, caption);
      return media;
    }

    buildContent(record) {
      const content = el('div', 'vk-record__content');
      content.append(
        el('p', 'vk-record__brand', record.brand || this.dataset.defaultBrand || 'Vault Aroma'),
        el('h2', 'vk-record__name', record.name)
      );

      const classification = [record.concentration, record.edition].filter(Boolean).join(' / ');
      if (classification) content.append(el('p', 'vk-record__classification', classification));

      const facts = el('dl', 'vk-record__facts');
      facts.append(this.fact('VaultKey', record.key || fullKey(record.digits)));
      if (record.formulationVersion) facts.append(this.fact('Formulation version', record.formulationVersion));
      if (record.verifiedOn) facts.append(this.fact('Documentation date', formatDate(record.verifiedOn)));
      if (record.recordStatus) facts.append(this.fact('Record status', record.recordStatus));
      content.append(facts, this.buildDocumentationStatus(record));

      if (record.statusNote) content.append(el('p', 'vk-record__status-note', record.statusNote));
      if (record.replacementKey) {
        content.append(el('p', 'vk-record__replacement', `Replaced by ${record.replacementKey}`));
      }

      const action = el('div', 'vk-record__action');
      if (record.productUrl) {
        const link = el('a', 'vk-record__product-link');
        link.href = record.productUrl;
        const label = record.productAvailable
          ? (this.dataset.productLinkLabel || 'View fragrance')
          : (this.dataset.soldOutLinkLabel || 'View fragrance details');
        link.append(el('span', '', label), svgArrow());
        action.append(link);
        if (record.priceText) action.append(el('span', 'vk-record__price', record.priceText));
      } else {
        action.append(el('span', 'vk-record__missing-link', this.dataset.productMissingLabel || 'Fragrance page connection pending.'));
      }
      content.append(action);

      return content;
    }

    buildDocumentationStatus(record) {
      const block = el('section', 'vk-record__assurance');
      block.append(el('h3', 'vk-record__assurance-title', this.dataset.statusHeading || 'Fragrance record documentation'));

      const list = el('ul', 'vk-record__assurance-list');
      const normalizedStatus = String(record.recordStatus || '').toLowerCase();
      const recordIsActive = normalizedStatus === 'active' || normalizedStatus === 'current';
      const recordStatusLabel = record.recordStatus
        ? `Record ${record.recordStatus}`
        : (this.dataset.defaultStatus || 'Record active');

      const indicators = [
        { label: recordStatusLabel, state: recordIsActive ? 'complete' : 'current' },
        { label: record.productUrl ? 'Fragrance linked' : 'Fragrance page pending', state: record.productUrl ? 'complete' : 'pending' },
        { label: record.ingredients ? 'Ingredient declaration available' : 'Ingredient declaration pending', state: record.ingredients ? 'complete' : 'pending' },
        { label: (record.verifiedOn || record.formulationVersion) ? 'Documentation archived' : 'Documentation pending', state: (record.verifiedOn || record.formulationVersion) ? 'complete' : 'pending' },
        { label: 'VaultKey assigned', state: 'complete' }
      ];

      indicators.forEach((indicator) => {
        const item = el('li', 'vk-record__assurance-item');
        item.dataset.state = indicator.state;
        item.append(el('span', 'vk-record__assurance-mark'), el('span', '', indicator.label));
        list.append(item);
      });

      block.append(list);
      return block;
    }

    buildDocumentationProcess() {
      const section = el('section', 'vk-record__process');
      const heading = el('div', 'vk-record__process-heading');
      heading.append(
        el('span', 'vk-record__section-number', '03'),
        el('h3', 'vk-record__section-title', this.dataset.processHeading || 'The Vault Aroma documentation process')
      );
      const intro = el('p', 'vk-record__process-intro', this.dataset.processIntro || 'Each fragrance record is maintained through a consistent documentation process from sourcing to record activation.');

      const list = el('ol', 'vk-record__process-list');
      [
        'Approved fragrance sourced',
        'Original bottle received',
        'Fragrance documentation archived',
        'Ingredient declaration recorded',
        'Fragrance prepared by Vault Aroma',
        'VaultKey assigned',
        'Fragrance record activated'
      ].forEach((label, index) => {
        const item = el('li', 'vk-record__process-step');
        item.append(
          el('span', 'vk-record__process-index', String(index + 1).padStart(2, '0')),
          el('span', 'vk-record__process-label', label)
        );
        list.append(item);
      });

      section.append(heading, intro, list);
      return section;
    }

    fact(label, value) {
      const item = el('div', 'vk-record__fact');
      item.append(el('dt', '', label), el('dd', '', value));
      return item;
    }

    buildIngredients(record) {
      const section = el('section', 'vk-record__ingredients');
      section.append(
        el('span', 'vk-record__section-number', '01'),
        el('h3', 'vk-record__section-title', this.dataset.ingredientsHeading || 'Ingredient declaration')
      );
      section.append(
        el('p', 'vk-record__ingredients-copy', record.ingredients || this.dataset.ingredientsPending || 'Ingredient declaration pending documentation.')
      );
      return section;
    }

    buildRecordNotes(record) {
      const section = el('section', 'vk-record__notes');
      section.append(
        el('span', 'vk-record__section-number', '02'),
        el('h3', 'vk-record__section-title', this.dataset.whyHeading || this.dataset.notesHeading || 'Why this record exists')
      );

      section.append(el('p', 'vk-record__notes-copy', this.dataset.whyText || this.dataset.defaultNote || 'Vault Aroma maintains fragrance records so customers can access the fragrance identity, ingredient declaration, and documented information connected to their purchase.'));
      if (record.warning) section.append(el('p', 'vk-record__warning', record.warning));

      return section;
    }

    async copyLink(button) {
      const previous = button.firstChild?.textContent || this.dataset.copyLabel || 'Copy record link';
      const url = new URL(window.location.href);
      url.searchParams.set('vk', this.currentRecord.key || fullKey(this.currentRecord.digits));

      try {
        await navigator.clipboard.writeText(url.toString());
        if (button.firstChild) button.firstChild.textContent = this.dataset.copiedLabel || 'Link copied';
      } catch (error) {
        if (button.firstChild) button.firstChild.textContent = this.dataset.copyFailedLabel || 'Copy failed';
      }

      window.setTimeout(() => {
        if (button.firstChild) button.firstChild.textContent = previous;
      }, 1800);
    }
  }

  if (!customElements.get('vaultkey-entry')) customElements.define('vaultkey-entry', VaultKeyEntry);
  if (!customElements.get('vaultkey-locator')) customElements.define('vaultkey-locator', VaultKeyLocator);
})();
